// src/pages/Impressum.tsx
import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ArrowLeft, Mail, Phone, MapPin, Shield } from 'lucide-react';

const Impressum: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();

  return (
    <div style={{
      minHeight: '100vh',
      background: '#FFF8F3',
      padding: '2rem'
    }}>
      <div style={{
        maxWidth: '900px',
        margin: '0 auto'
      }}>
        {/* Back Button */}
        <button
          onClick={() => navigate(-1)}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            padding: '8px 16px',
            background: 'transparent',
            border: 'none',
            color: 'var(--vf-primary)',
            fontSize: '1rem',
            fontWeight: 600,
            cursor: 'pointer',
            marginBottom: '1rem',
            transition: 'all 0.3s'
          }}
        >
          <ArrowLeft size={20} />
          {t('common.back')}
        </button>

        {/* Header */}
        <div style={{
          background: 'white',
          borderRadius: '15px',
          padding: '2rem',
          marginBottom: '1.5rem',
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
        }}>
          <h1 style={{
            fontSize: '2.5rem',
            marginBottom: '0.5rem',
            color: '#2E3A4B'
          }}>
            📋 {t('impressum.title')}
          </h1>
          <p style={{ color: '#666', marginBottom: 0 }}>
            {t('impressum.subtitle')}
          </p>
        </div>

        {/* Company Information */}
        <div style={{
          background: 'white',
          borderRadius: '15px',
          padding: '2rem',
          marginBottom: '1.5rem',
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
        }}>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '0.75rem',
            marginBottom: '1.5rem'
          }}>
            <MapPin size={24} color="var(--vf-primary)" />
            <h2 style={{ margin: 0, fontSize: '1.5rem' }}>{t('impressum.company.title')}</h2>
          </div>

          <div style={{ lineHeight: '1.8', color: '#2E3A4B' }}>
            <p style={{ margin: '0.5rem 0', fontWeight: 600, fontSize: '1.1rem' }}>
              {t('impressum.company.name')}
            </p>
            <p style={{ margin: '0.5rem 0' }}>
              {t('impressum.company.street')}
            </p>
            <p style={{ margin: '0.5rem 0' }}>
              {t('impressum.company.city')}
            </p>
            <p style={{ margin: '0.5rem 0' }}>
              {t('impressum.company.country')}
            </p>
          </div>
        </div>

        {/* Contact Information */}
        <div style={{
          background: 'white',
          borderRadius: '15px',
          padding: '2rem',
          marginBottom: '1.5rem',
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
        }}>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '0.75rem',
            marginBottom: '1.5rem'
          }}>
            <Mail size={24} color="var(--vf-primary)" />
            <h2 style={{ margin: 0, fontSize: '1.5rem' }}>{t('impressum.contact.title')}</h2>
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '1rem',
              padding: '1rem',
              background: '#F3F4F6',
              borderRadius: '8px'
            }}>
              <Mail size={20} color="var(--vf-primary)" />
              <div>
                <div style={{ fontWeight: 600, color: '#2E3A4B', marginBottom: '0.25rem' }}>
                  {t('impressum.contact.email')}
                </div>
                <a
                  href={`mailto:${t('impressum.contact.emailValue')}`}
                  style={{
                    color: 'var(--vf-primary)',
                    textDecoration: 'none',
                    fontWeight: 500
                  }}
                >
                  {t('impressum.contact.emailValue')}
                </a>
              </div>
            </div>

            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '1rem',
              padding: '1rem',
              background: '#F3F4F6',
              borderRadius: '8px'
            }}>
              <Phone size={20} color="var(--vf-primary)" />
              <div>
                <div style={{ fontWeight: 600, color: '#2E3A4B', marginBottom: '0.25rem' }}>
                  {t('impressum.contact.phone')}
                </div>
                <a
                  href={`tel:${t('impressum.contact.phoneValue')}`}
                  style={{
                    color: 'var(--vf-primary)',
                    textDecoration: 'none',
                    fontWeight: 500
                  }}
                >
                  {t('impressum.contact.phoneValue')}
                </a>
              </div>
            </div>
          </div>
        </div>

        {/* Legal Information */}
        <div style={{
          background: 'white',
          borderRadius: '15px',
          padding: '2rem',
          marginBottom: '1.5rem',
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
        }}>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '0.75rem',
            marginBottom: '1.5rem'
          }}>
            <Shield size={24} color="var(--vf-primary)" />
            <h2 style={{ margin: 0, fontSize: '1.5rem' }}>{t('impressum.legal.title')}</h2>
          </div>

          <div style={{ lineHeight: '1.8', color: '#2E3A4B' }}>
            <p style={{ margin: '0.5rem 0' }}>
              <strong>{t('impressum.legal.representative')}:</strong> {t('impressum.legal.representativeValue')}
            </p>
            <p style={{ margin: '0.5rem 0' }}>
              <strong>{t('impressum.legal.register')}:</strong> {t('impressum.legal.registerValue')}
            </p>
            <p style={{ margin: '0.5rem 0' }}>
              <strong>{t('impressum.legal.vat')}:</strong> {t('impressum.legal.vatValue')}
            </p>
          </div>
        </div>

        {/* Privacy & Data Protection */}
        <div style={{
          background: 'white',
          borderRadius: '15px',
          padding: '2rem',
          marginBottom: '1.5rem',
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
        }}>
          <h2 style={{ fontSize: '1.5rem', marginBottom: '1rem', color: '#2E3A4B' }}>
            🔒 {t('impressum.privacy.title')}
          </h2>

          <div style={{ lineHeight: '1.8', color: '#666' }}>
            <h3 style={{ color: '#2E3A4B', fontSize: '1.2rem', marginTop: '1.5rem', marginBottom: '0.75rem' }}>
              {t('impressum.privacy.collection.title')}
            </h3>
            <p>{t('impressum.privacy.collection.text')}</p>

            <h3 style={{ color: '#2E3A4B', fontSize: '1.2rem', marginTop: '1.5rem', marginBottom: '0.75rem' }}>
              {t('impressum.privacy.usage.title')}
            </h3>
            <ul style={{ paddingLeft: '1.5rem' }}>
              <li>{t('impressum.privacy.usage.item1')}</li>
              <li>{t('impressum.privacy.usage.item2')}</li>
              <li>{t('impressum.privacy.usage.item3')}</li>
              <li>{t('impressum.privacy.usage.item4')}</li>
            </ul>

            <h3 style={{ color: '#2E3A4B', fontSize: '1.2rem', marginTop: '1.5rem', marginBottom: '0.75rem' }}>
              {t('impressum.privacy.storage.title')}
            </h3>
            <p>{t('impressum.privacy.storage.text')}</p>

            <h3 style={{ color: '#2E3A4B', fontSize: '1.2rem', marginTop: '1.5rem', marginBottom: '0.75rem' }}>
              {t('impressum.privacy.rights.title')}
            </h3>
            <p>{t('impressum.privacy.rights.text')}</p>
            <ul style={{ paddingLeft: '1.5rem' }}>
              <li>{t('impressum.privacy.rights.item1')}</li>
              <li>{t('impressum.privacy.rights.item2')}</li>
              <li>{t('impressum.privacy.rights.item3')}</li>
              <li>{t('impressum.privacy.rights.item4')}</li>
            </ul>

            <h3 style={{ color: '#2E3A4B', fontSize: '1.2rem', marginTop: '1.5rem', marginBottom: '0.75rem' }}>
              {t('impressum.privacy.cookies.title')}
            </h3>
            <p>{t('impressum.privacy.cookies.text')}</p>

            <h3 style={{ color: '#2E3A4B', fontSize: '1.2rem', marginTop: '1.5rem', marginBottom: '0.75rem' }}>
              {t('impressum.privacy.thirdparty.title')}
            </h3>
            <p>{t('impressum.privacy.thirdparty.text')}</p>
            <ul style={{ paddingLeft: '1.5rem' }}>
              <li><strong>Google Firebase:</strong> {t('impressum.privacy.thirdparty.firebase')}</li>
              <li><strong>Google Maps:</strong> {t('impressum.privacy.thirdparty.maps')}</li>
            </ul>
          </div>
        </div>

        {/* Disclaimer */}
        <div style={{
          background: 'white',
          borderRadius: '15px',
          padding: '2rem',
          marginBottom: '1.5rem',
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
        }}>
          <h2 style={{ fontSize: '1.5rem', marginBottom: '1rem', color: '#2E3A4B' }}>
            ⚖️ {t('impressum.disclaimer.title')}
          </h2>

          <div style={{ lineHeight: '1.8', color: '#666' }}>
            <h3 style={{ color: '#2E3A4B', fontSize: '1.2rem', marginBottom: '0.75rem' }}>
              {t('impressum.disclaimer.content.title')}
            </h3>
            <p>{t('impressum.disclaimer.content.text')}</p>

            <h3 style={{ color: '#2E3A4B', fontSize: '1.2rem', marginTop: '1.5rem', marginBottom: '0.75rem' }}>
              {t('impressum.disclaimer.links.title')}
            </h3>
            <p>{t('impressum.disclaimer.links.text')}</p>

            <h3 style={{ color: '#2E3A4B', fontSize: '1.2rem', marginTop: '1.5rem', marginBottom: '0.75rem' }}>
              {t('impressum.disclaimer.copyright.title')}
            </h3>
            <p>{t('impressum.disclaimer.copyright.text')}</p>
          </div>
        </div>

        {/* Footer */}
        <div style={{
          textAlign: 'center',
          padding: '2rem 0',
          color: '#999',
          fontSize: '0.9rem'
        }}>
          <p>© {new Date().getFullYear()} VoltFox - {t('impressum.footer')}</p>
          <p style={{ marginTop: '0.5rem' }}>
            <a
              href="https://mr-vision.ch"
              target="_blank"
              rel="noopener noreferrer"
              style={{
                color: 'var(--vf-primary)',
                textDecoration: 'none',
                fontWeight: 600
              }}
            >
              {t('common.createdBy')} ✨
            </a>
          </p>
        </div>
      </div>
    </div>
  );
};

export default Impressum;
